import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormHelperText } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';
import { KeyboardArrowDown } from '@material-ui/icons';

import { useGetCountryList } from '@entities/country/api';

import s from './StepFour.module.css';
import { ButtonContainer } from '../../../Button/Button';
import { CityInput } from '../../../CityInput/CityInput';
import { CountriesInput } from '../../../CountriesInput';
import { FormTitle } from '../../FormTitle/FormTitle';
import { Form } from '../Form';

const useStyles = makeStyles((theme) => ({
    formControl: {
        borderBottom: 'none',
        position: 'relative',
        '& .MuiFormControl-root.MuiTextField-root': {
            marginBottom: '30px !important',
        },
        '& .MuiList-root': {
            height: '300px',
        },
        '& .MuiInputBase-root': {
            border: '1px solid transparent',
            fontSize: 16,
            borderRadius: 6,
        },
        '& .MuiFormLabel-root': {
            color: '#8692A6',
        },
        '& .MuiInputBase-root:hover': {
            border: '1px solid #DCDCDC',
        },
        '& .MuiInputBase-root #placeholder': {
            fontSize: '16px',
        },
        '& .MuiFilledInput-root': {
            backgroundColor: '#F7F7F7',
            borderRadius: '6px',
        },
        '& .MuiInputBase-root.Mui-error': {
            border: '1px solid #F90000',
        },
        '& .MuiInputBase-root:not(.Mui-error):hover': {
            border: '1px solid #DCDCDC',
        },
        '& .MuiInputBase-root:not(.Mui-error).Mui-focused': {
            border: '1px solid #1094F7',
            backgroundColor: 'transparent',
        },
        '& .MuiFormHelperText-root': {
            lineHeight: '16px',
        },
        '& .MuiFormHelperText-root.Mui-error': {
            color: '#F90000',
            fontSize: 14,
            lineHeight: '16px',
        },
        '& .MuiInputBase-input.MuiFilledInput-input': {
            borderRadius: '6px',
            padding: '29px 12px 10px',
            [theme.breakpoints.down('xs')]: {
                borderRadius: '6px',
                padding: '27px 12px 8px',
            },
        },
        '& .MuiFilledInput-underline:after': {
            border: 'none',
        },
        '& .MuiFilledInput-underline:before': {
            border: 'none',
        },
    },
    error: {
        backgroundColor: '#F7F7F7',
        borderRadius: '6px',
        border: '1px solid red',
        '&.MuiSelect-select:focus': {
            backgroundColor: '#F7F7F7',
            borderRadius: '6px',
        },
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
}));

const useMenuListStyles = makeStyles((theme) => ({
    root: {
        '& MuiListItem-root.Mui-focusVisible': {
            backgroundColor: 'rgba(0, 0, 0, 0)',
        },
        height: 300,
        width: '100%',
        '& .MuiListItem-root.Mui-selected': {
            color: '#1094F7',
            borderLeft: '2px solid #1094F7',
            backgroundColor: 'transparent',
        },
        '& .MuiListItem-root:hover': {
            backgroundColor: '#F2F6F7',
        },
        '& .MuiListItem-root': {
            fontSize: '16px',
            whiteSpace: 'normal',
            [theme.breakpoints.down('xs')]: {
                fontSize: 14,
                lineHeight: '15px',
                minHeight: 35,
            },
        },
    },
}));

export const StepFour = ({
    state,
    updateActivity,
    updateCity,
    updateCountry,
    sendValidateFourthStepRequest,
    activitiesList,
    cityList,
}) => {
    const [selectTouched, setSelectTouched] = useState(false);
    const [errorSelect, setErrorSelect] = useState(false);
    const [errors, setErrors] = useState({
        city: null,
        activity: null,
        country: null,
    });
    const { data } = useGetCountryList();
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    // useEffect(() => {
    //     console.log('countryList', data);
    //     console.log('LOCALE', LOCALE);
    // }, [data]);

    const writeError = useCallback((value) => {
        if (!value) {
            setErrors((prevState) => ({
                ...prevState,
                activity: i18next.t('StepFour.return.writeError'),
            }));
        } else {
            setErrors((prevState) => ({ ...prevState, activity: null }));
        }
    }, []);

    useEffect(() => {
        if (selectTouched) {
            writeError(state.activity);
        }
    }, [selectTouched, state.activity, writeError]);

    useEffect(() => {
        if (LOCALE === 'ru') {
            setIsButtonDisabled(state.city === '' && state.activity === '');
        } else {
            setIsButtonDisabled(state.activity === '' && state.country === '');
        }
    }, [state.city, state.activity, state.country]);

    const classes = useStyles();
    const menuListClasses = useMenuListStyles();

    const someRef = useRef(null);

    const menuProps = useMemo(
        () => ({
            anchorEl: () => someRef.current,
            MenuListProps: {
                classes: menuListClasses,
            },
            variant: 'menu',
            'data-id': 'activity-options-list',
        }),
        [menuListClasses]
    );

    const buttonClick = useCallback(() => {
        if (state.activity && (LOCALE === 'ru' ? state.city : state.country)) {
            sendValidateFourthStepRequest();
        } else if (!state.activity) {
            setErrors((prevState) => ({
                ...prevState,
                activity: i18next.t('StepFour.return.buttonClick.activity'),
            }));
            setErrorSelect(true);
        } else if (!state.city) {
            setErrors((prevState) => ({
                ...prevState,
                city: i18next.t('StepFour.return.buttonClick.city'),
            }));
        } else if (!state.country) {
            setErrors((prevState) => ({
                ...prevState,
                country: i18next.t('StepFour.return.buttonClick.city'),
            }));
        }
    }, [sendValidateFourthStepRequest, state.activity, state.city, state.country]);

    // const isButtonDisabled = state.city === '' && state.activity === '' && state.country === '';

    return (
        <>
            <FormTitle title={i18next.t('StepFour.return.FormTitle.title')} />
            {activitiesList && cityList && (
                <Form>
                    {LOCALE === 'ru' && (
                        <CityInput
                            updateCity={updateCity}
                            cityList={cityList}
                            setErrors={setErrors}
                            errors={errors}
                        />
                    )}
                    {LOCALE !== 'ru' && (
                        <CountriesInput
                            updateCountry={updateCountry}
                            countryList={data?.data}
                            setErrors={setErrors}
                            errors={errors}
                        />
                    )}

                    <FormControl
                        variant="filled"
                        className={classes.formControl}
                        error={errorSelect}
                    >
                        <InputLabel id="activity-label">
                            {i18next.t('StepFour.return.InputLabel')}
                        </InputLabel>
                        <Select
                            label={i18next.t('StepFour.return.InputLabel')}
                            labelId="activity-label"
                            id="activity"
                            required
                            data-id="activity-input"
                            IconComponent={KeyboardArrowDown}
                            value={state.activity}
                            onChange={(e) => {
                                updateActivity(e.target.value);
                                setSelectTouched(true);
                            }}
                            onClose={(event) => {
                                setSelectTouched(true);
                                if (
                                    event.target.getAttribute('data-value') === null &&
                                    state.activity === ''
                                ) {
                                    setErrorSelect(true);
                                } else {
                                    setErrorSelect(false);
                                }
                            }}
                            MenuProps={menuProps}
                        >
                            {activitiesList.map((activity) => (
                                <MenuItem value={activity.id} key={activity.id}>
                                    {activity.name}
                                </MenuItem>
                            ))}
                        </Select>
                        <div className={s.errorText}>
                            {errorSelect && <FormHelperText>{errors.activity}</FormHelperText>}
                        </div>
                        <div ref={someRef} className={s.startSelectListPosition} />
                    </FormControl>
                    <ButtonContainer
                        textButton={i18next.t('StepFour.return.ButtonContainer.textButton')}
                        disable={isButtonDisabled}
                        onClick={buttonClick}
                        needShowLoader={state.needShowLoader}
                    />
                </Form>
            )}
        </>
    );
};
